import i18n from "@/i18n/i18n";
import React from "react";
import type { SearchRow } from "@/services/api/search";
import { useSearchPanelOptimized } from "@/hooks/useSearchPanelOptimized";
import { useNavigate } from "react-router-dom";
import { App as AntApp } from "antd";

export interface UseSearchIndexResult {
  keyword: string;
  setKeyword: (v: string) => void;
  rows: SearchRow[];
  isLoading: boolean;
  error: string | null;
  rowsOpen: boolean;
  onCloseRows: () => void;
  searchOnce: (kw?: string) => Promise<SearchRow[]>;
  buildNavigateParamsFromRow: (
    row: SearchRow,
    currentKeyword: string
  ) => URLSearchParams;
  onSearch: (kw?: string | SearchRow) => Promise<void>;
  onSelectRow: (row: SearchRow) => void;
}

/**
 * 优化版搜索首页逻辑Hook
 * 减少重复API调用，提升用户体验
 */
export function useSearchIndexOptimized(): UseSearchIndexResult {
  const { keyword, setKeyword, rows, search, isLoading, error } =
    useSearchPanelOptimized("");
  const navigate = useNavigate();
  const { message } = AntApp.useApp();

  const [rowsOpen, setRowsOpen] = React.useState(false);
  const [shouldAutoOpenRows, setShouldAutoOpenRows] = React.useState(true);

  // 优化下拉展开逻辑
  React.useEffect(() => {
    // 只有在shouldAutoOpenRows为true时才自动打开下拉列表
    if (shouldAutoOpenRows) {
      // 修改逻辑：有搜索结果就显示，不管是否有关键词（支持默认数据）
      const shouldOpen = Boolean(rows.length && !isLoading);
      setRowsOpen(shouldOpen);
    }
  }, [keyword, rows.length, isLoading, shouldAutoOpenRows]);

  const onCloseRows = React.useCallback(() => {
    setRowsOpen(false);
    setShouldAutoOpenRows(false);
  }, []);

  // 搜索建议函数 - 复用现有搜索逻辑
  const searchOnce = React.useCallback(
    async (kw?: string) => {
      const result = await search(kw);
      // 当搜索完成且有结果时，确保展开下拉列表并启用自动打开
      if (result && result.length > 0) {
        setRowsOpen(true);
        setShouldAutoOpenRows(true);
      }
      return result || [];
    },
    [search]
  );

  const buildNavigateParamsFromRow = React.useCallback(
    (row: SearchRow, currentKeyword: string) => {
      console.log("🔗 构建导航参数:", { row, currentKeyword });
      const params = new URLSearchParams({
        brand: row.brand,
        brandId: row.brandId || "",
        industry: row.currentCategoryName || "",
        industryId: row.industryId || "",
        industries: JSON.stringify(row.tags || []),
        industryIds: JSON.stringify(row.industryIds || []),
        q: row.brand, // 使用品牌名称而不是搜索关键字
        modelName: "all",
      });
      console.log("🔗 最终导航参数:", params.toString());
      return params;
    },
    []
  );

  // 优化搜索逻辑 - 避免重复API调用
  const onSearch = React.useCallback(
    async (kw?: string | SearchRow) => {
      // 如果传入的是SearchRow对象，直接使用它
      if (typeof kw === "object" && kw !== null && "brand" in kw) {
        const row = kw as SearchRow;
        const params = buildNavigateParamsFromRow(row, row.brand);
        navigate(`/SearchDetail?${params.toString()}`);
        // 不立即关闭下拉列表，让点击处理函数来控制
        return;
      }

      const searchKeyword = (kw as string) || keyword || "";

      // 立即关闭下拉列表并禁用自动打开
      setRowsOpen(false);
      setShouldAutoOpenRows(false);

      try {
        // 如果已有搜索结果且关键词匹配，直接使用
        if (rows.length > 0 && (searchKeyword === keyword || !searchKeyword)) {
          console.log("🚀 使用现有搜索结果，避免重复API调用");
          const row = rows[0];
          const params = buildNavigateParamsFromRow(row, searchKeyword);
          navigate(`/SearchDetail?${params.toString()}`);
          // 不再清空搜索框，让用户可以继续编辑
          // setKeyword('')
          return;
        }

        // 否则执行新搜索
        const searchResults = await search(searchKeyword);
        if (searchResults && searchKeyword.length > 0) {
          if (searchResults.length > 0) {
            const row = searchResults[0];
            const params = buildNavigateParamsFromRow(row, searchKeyword);
            navigate(`/SearchDetail?${params.toString()}`);
            // 不再清空搜索框，让用户可以继续编辑
            // setKeyword('')
          } else {
            message.info(i18n.t("search_detail.no_brand_tip"));
          }
        }
      } catch (e) {
        console.error("搜索错误:", e);
      }
    },
    [
      search,
      keyword,
      rows,
      buildNavigateParamsFromRow,
      navigate,
      message,
      setRowsOpen,
    ]
  );

  const onSelectRow = React.useCallback(
    async (row: SearchRow) => {
      console.log("👆 首页点击选择行:", row);
      console.log("📋 行数据完整性检查:", {
        hasBrandId: !!row.brandId,
        hasIndustryId: !!row.industryId,
        brandId: row.brandId,
        industryId: row.industryId,
      });

      // 如果行数据缺少必要信息，先执行搜索获取完整数据
      if (!row.brandId || !row.industryId) {
        console.log("⚠️ 行数据不完整，执行搜索获取完整数据");
        try {
          const searchResults = await search(row.brand);
          console.log("🔍 搜索结果:", searchResults);
          if (searchResults && searchResults.length > 0) {
            // 使用搜索结果中的第一个匹配项
            const completeRow = searchResults[0];
            console.log("✅ 获取到完整数据:", completeRow);
            const params = buildNavigateParamsFromRow(
              completeRow,
              keyword || ""
            );
            console.log("🚀 准备导航到:", params.toString());
            navigate(`/SearchDetail?${params.toString()}`);
          } else {
            console.log("❌ 搜索无结果");
            message.info(i18n.t("search_detail.no_brand_tip"));
          }
        } catch (e) {
          console.error("❌ 搜索错误:", e);
          message.error("获取品牌信息失败");
        }
      } else {
        // 数据完整，直接导航
        console.log("✅ 数据完整，直接导航");
        const params = buildNavigateParamsFromRow(row, keyword || "");
        console.log("🚀 准备导航到:", params.toString());
        navigate(`/SearchDetail?${params.toString()}`);
      }

      // 关闭下拉列表并禁用自动打开
      console.log("🔒 关闭下拉列表");
      setRowsOpen(false);
      setShouldAutoOpenRows(false);
      // 不再清空搜索框，让用户可以继续编辑
      // setKeyword('')
    },
    [
      buildNavigateParamsFromRow,
      keyword,
      navigate,
      search,
      message,
      setRowsOpen,
    ]
  );

  return {
    keyword,
    setKeyword,
    rows,
    isLoading,
    error,
    rowsOpen,
    onCloseRows,
    searchOnce,
    buildNavigateParamsFromRow,
    onSearch,
    onSelectRow,
  };
}

export default useSearchIndexOptimized;
